import { backendURL, nascsubdomain, trainingsubdomain } from 'redux/reducers/settings';
import getDoaminName from './getDomainName';

/**
 * Per tenant branding for the metadata a crawler / browser tab reads
 * (title, description, favicon, manifest). Everything here is keyed by the
 * first label of the host, the same way the app already picks its tenant --
 * see {@link getDoaminName} and the `Domain` header sent with every request.
 */
export interface SiteBranding {
  /** subdomain the branding belongs to */
  key: string;
  organization: string;
  shortName: string;
  description: string;
  themeColor: string;
  /** folder under `public/brand` holding favicon.ico, logo192/512.png and manifest.json */
  assetDir: string;
}

const nascBranding: SiteBranding = {
  key: nascsubdomain,
  organization: 'Nepal Administrative Staff College',
  shortName: 'NASC TMS',
  description:
    'Training Management System (TMS) of Nepal Administrative Staff College (NASC) - manage trainings, courses, participants and results.',
  themeColor: '#0b3b8c',
  assetDir: 'nasc'
};

const nartcBranding: SiteBranding = {
  key: trainingsubdomain,
  organization: 'National Ayurveda Research and Training Center',
  shortName: 'NARTC TMS',
  description:
    'Training Management System (TMS) of National Ayurveda Research and Training Center (NARTC) - manage trainings, courses, participants and results.',
  themeColor: '#0b3b8c',
  assetDir: 'nartc'
};

const brandingBySubdomain: Record<string, SiteBranding> = {
  [nascsubdomain]: nascBranding,
  [trainingsubdomain]: nartcBranding
};

/** Used on localhost and on any host that is not a known tenant. */
export const defaultBranding = nascBranding;

export const getSubdomain = () =>
  process.env.NODE_ENV === 'development' ? nascsubdomain : getDoaminName() ?? '';

export const getBranding = (subdomain: string = getSubdomain()): SiteBranding =>
  brandingBySubdomain[subdomain] ?? defaultBranding;

/** Values the API can override once the organization config is loaded. */
export interface BrandingOverrides {
  title?: string;
  logo?: string;
}

const publicUrl = process.env.PUBLIC_URL ?? '';

const setMeta = (attr: 'name' | 'property', key: string, content: string) => {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const setLinks = (selector: string, href: string) => {
  const links = document.head.querySelectorAll<HTMLLinkElement>(selector);
  links.forEach((link) => link.setAttribute('href', href));
  return links.length;
};

/**
 * Point the document metadata at the tenant that is being served.
 *
 * Called once before render with the static branding above so the tab is right
 * on first paint, then again from the organization context with whatever the
 * API reports, so a tenant that is not hard coded here still gets its own
 * title and logo.
 */
const applyBranding = (overrides?: BrandingOverrides) => {
  if (typeof document === 'undefined') return;

  const subdomain = getSubdomain();
  const branding = getBranding(subdomain);
  const organization = overrides?.title || branding.organization;
  const description = overrides?.title
    ? `Training Management System (TMS) of ${overrides.title} - manage trainings, courses, participants and results.`
    : branding.description;

  // known tenants ship hand made icons under public/brand; anyone else falls
  // back to whatever logo the backend has on file for the domain
  const assets = `${publicUrl}/brand/${branding.assetDir}`;
  const remoteLogo =
    !(subdomain in brandingBySubdomain) && overrides?.logo ? backendURL + overrides.logo : '';
  const icon = remoteLogo || `${assets}/favicon.ico`;
  const largeIcon = remoteLogo || `${assets}/logo512.png`;

  document.title = `TMS | ${organization}`;
  document.documentElement.lang = 'en';

  setMeta('name', 'description', description);
  setMeta('name', 'theme-color', branding.themeColor);
  setMeta('property', 'og:type', 'website');
  setMeta('property', 'og:site_name', branding.shortName);
  setMeta('property', 'og:title', `TMS - ${organization}`);
  setMeta('property', 'og:description', description);
  setMeta('property', 'og:image', largeIcon);
  setMeta('name', 'twitter:card', 'summary');

  if (!setLinks('link[rel~="icon"]', icon)) {
    const link = document.createElement('link');
    link.rel = 'icon';
    link.href = icon;
    document.head.appendChild(link);
  }
  setLinks('link[rel="apple-touch-icon"]', remoteLogo || `${assets}/logo192.png`);
  setLinks('link[rel="manifest"]', `${assets}/manifest.json`);
};

export default applyBranding;
